import { getSizeOrder, normalizeSize, normalizeTextKey, type StockMode } from '@threadheads/shared';
import { toCsvContent, UTF8_BOM } from '@/lib/csv';
import type { SupplyAliasInfo } from '../types';

/**
 * The stocktake count sheet — ONE generator for all three outputs (export
 * selected, export all shown, blank example), so they cannot drift.
 *
 * Column order is locked by the product owner:
 *   Sku, Stock Count, Title, Production Type, Product Type, Size, Color
 * `Owner` and `Location` are APPENDED (8, 9) so the locked order is undisturbed.
 *
 * `Stock Count` ships BLANK by default. Pre-filling it made an ordinary count a
 * silent rollback: export 5,000 rows at 09:00, count 200, re-import at 15:00,
 * and the 4,800 rows nobody touched are written back to their 09:00 values —
 * erasing every sale, receipt and transfer in between. The wizard submits every
 * preview row and `atomic_set_stock_v2` has no zero-delta guard, so those are
 * real writes. Blank makes "blank = not counted" load-bearing.
 */

export type StocktakeBlock = 'POD' | 'PAP' | 'PREPRINTED' | 'TRIM';

/** Structural input — the frontend `Product` satisfies this with no mapping. */
export interface StocktakeCsvSource {
  /** Row identity. The final tiebreak — `sku` is NOT unique across orgs. */
  id: string;
  sku: string;
  title?: string;
  stockLevel: number;
  productionType: 'PAP' | 'POD';
  isPreprinted?: boolean;
  isTrim?: boolean;
  ownerOrganizationId?: string;
  category: { type?: string; size?: string; color?: string };
  /**
   * BLU-711 Part B: substitution-mapping pairing key (= the mapping row's id),
   * present on BOTH halves of a junction-valid shared-SKU pair. Post-split the
   * two orgs hold same-CODE rows for one physical pile — without collapsing,
   * the counter sees the same shelf item twice and double-counts it. The
   * server stamps it only when the two codes match: a pair with different
   * codes is two piles and carries none, so each half is its own line.
   */
  sharedPairId?: string | null;
  sharedPairRole?: 'client' | 'owner' | null;
  /**
   * Phase 92 (D-13): present on a supply alias, a client product built on the
   * 3PL's blank. It holds no stock of its own (the pile is the blank's), so
   * it never goes on a count sheet; the server refuses a counted alias row.
   */
  supplyAlias?: SupplyAliasInfo | null;
  /**
   * BLU-1268: how the pair is supplied here. A Just in time pair's stock is
   * not counted (its figures are the store constant, 9999), so it never goes
   * on a count sheet or into a stocktake; the server refuses a counted one.
   */
  stockMode?: StockMode | null;
}

/** A Just in time pair: no shelf count to take, so every stocktake surface leaves it out. */
export const isJustInTimePair = (row: Pick<StocktakeCsvSource, 'stockMode'>): boolean =>
  row.stockMode === 'just_in_time';

/**
 * The rows a count sheet can hold. Aliases leave BEFORE the pair collapse: an
 * alias never pairs, and its stock is counted on the blank's own line. Just in
 * time pairs leave too: there is nothing on the shelf to count, and a
 * pre-filled Stock Count would print the store constant (9999) as if it were
 * stock.
 */
const countableRows = <T extends StocktakeCsvSource>(rows: readonly T[]): T[] =>
  rows.filter((row) => !row.supplyAlias && !isJustInTimePair(row));

/** What a count sheet of these rows holds: its lines, and the Just in time pairs left out. */
export interface CountSheetSize {
  lines: number;
  justInTime: number;
}

/**
 * The size of the sheet `buildStocktakeCsv` writes for these rows, so a menu
 * label or a toast states the file's own line count, never the rows on screen
 * ("Count sheet: all 8" over a 3-line file).
 */
export function countSheetSize(rows: readonly StocktakeCsvSource[]): CountSheetSize {
  return {
    lines: collapseSharedPairs(countableRows(rows)).length,
    justInTime: rows.filter(isJustInTimePair).length,
  };
}

export const STOCKTAKE_COLUMN_COUNT = 10;

/** Canonical (English) header names. The zh sheet uses translated labels. */
export const STOCKTAKE_CANONICAL_HEADERS = [
  'Sku',
  'Stock Count',
  'Title',
  'Production Type',
  'Product Type',
  'Size',
  'Color',
  'Owner',
  'Owner ID',
  'Location',
] as const;

/**
 * Import alias map: header text (lowercased; `_` folded to a space by the
 * canonicaliser) -> canonical column name.
 *
 * Lives next to the export headers on purpose — the sheet we WRITE and the
 * headers we ACCEPT must never drift. Covers en, zh, and the legacy
 * `sku,stock` template plus `downloadStockCsv`'s `current_stock`.
 *
 * Deliberately NOT aliased: `on hand`, `available`, `committed`, `unavailable`,
 * `incoming`. The legacy 12-column stock export carries all of them AND
 * `current_stock`; aliasing any to `stock` would collide and hard-fail an
 * otherwise valid file. `barcode` is a different identifier, not a sku.
 */
export const STOCKTAKE_COLUMN_ALIASES: Record<string, string> = {
  // --- sku ---
  sku: 'sku',
  'sku code': 'sku',
  'item sku': 'sku',
  'product sku': 'sku',
  'sku编码': 'sku',
  编码: 'sku',
  货号: 'sku',
  // --- stock ---
  stock: 'stock',
  'stock count': 'stock',
  'current stock': 'stock',
  'new stock': 'stock',
  count: 'stock',
  qty: 'stock',
  quantity: 'stock',
  库存数量: 'stock',
  库存: 'stock',
  数量: 'stock',
  盘点数量: 'stock',
  // --- owner (cross-org routing) ---
  owner: 'owner',
  'owner organization': 'owner',
  'owner org': 'owner',
  所属组织: 'owner',
  组织: 'owner',
  客户: 'owner',
  // --- owner id: the AUTHORITATIVE routing key ---
  // Names are ambiguous (two orgs at one location can share a display name)
  // and editable. The id column is what the importer trusts; the name column
  // stays for the human counting the shelf.
  'owner id': 'ownerId',
  'owner organization id': 'ownerId',
  'organization id': 'ownerId',
  'org id': 'ownerId',
  所属组织id: 'ownerId',
  组织id: 'ownerId',
  // --- location (mismatch guard) ---
  location: 'location',
  warehouse: 'location',
  仓库: 'location',
  仓库名称: 'location',
};

export interface StocktakeCsvLabels {
  /** Nine header cells, in locked column order, in the viewer's language. */
  headers: readonly string[];
  /** Display label per block, e.g. en `Pre-printed` / zh `预印`. */
  blockLabels: Record<StocktakeBlock, string>;
}

export interface BuildStocktakeCsvOptions {
  labels: StocktakeCsvLabels;
  locationName: string;
  /** Owner org id -> display name, for cross-org rows at a shared 3PL location. */
  ownerNameById?: ReadonlyMap<string, string>;
  /** Name used when a row carries no `ownerOrganizationId` (single-tenant). */
  fallbackOwnerName: string;
  /** Caller's own org id, written to `Owner ID` for rows with no explicit owner. */
  ownOrganizationId?: string;
  /**
   * Opt-in: pre-fill `Stock Count` with current stock instead of leaving it
   * blank. Negatives clamp to 0 — a negative balance means the goods are not
   * physically on the shelf. Off by default; see the file docblock.
   */
  includeCurrentCounts?: boolean;
}

const BLOCK_RANK: Record<StocktakeBlock, number> = {
  POD: 0,
  PAP: 1,
  PREPRINTED: 2,
  TRIM: 3,
};

/**
 * Locale pinned to 'en' on purpose: the sheet's row order must be a property of
 * the DATA, not of the viewer's browser language. Otherwise the same export
 * diffs dirty after an EN/中文 switch. `numeric` so TEE-2 sorts before TEE-10.
 */
const COLLATOR = new Intl.Collator('en', { sensitivity: 'base', numeric: true });

const byCodepoint = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

/** Empty-last, then collated. Mirrors `compareText` in picklist-sort.ts. */
const cmpText = (a: string, b: string): number => {
  if (a === '' || b === '') return a === b ? 0 : a === '' ? 1 : -1;
  return COLLATOR.compare(a, b);
};

/**
 * `isPreprinted` is checked BEFORE `productionType` deliberately. The pre-print
 * write path sets `{ production_type: 'PAP', is_preprinted: true }` atomically,
 * so on real data the two orderings agree — but if a partial backfill ever
 * leaves `POD + is_preprinted`, this keeps the row out of the POD block rather
 * than sub-sorting it by a colour it does not meaningfully have.
 * `isTrim` wins outright, mirroring the badge precedence in InventoryTableList.
 */
export function getStocktakeBlock(p: StocktakeCsvSource): StocktakeBlock {
  if (p.isTrim) return 'TRIM';
  if (p.isPreprinted) return 'PREPRINTED';
  return p.productionType === 'PAP' ? 'PAP' : 'POD';
}

export function compareStocktakeRows(a: StocktakeCsvSource, b: StocktakeCsvSource): number {
  const ra = BLOCK_RANK[getStocktakeBlock(a)];
  const rb = BLOCK_RANK[getStocktakeBlock(b)];
  if (ra !== rb) return ra - rb;

  if (ra === BLOCK_RANK.POD) {
    const t = cmpText(a.category.type ?? '', b.category.type ?? '');
    if (t !== 0) return t;

    const c = cmpText(
      normalizeTextKey(a.category.color ?? ''),
      normalizeTextKey(b.category.color ?? ''),
    );
    if (c !== 0) return c;

    // normalizeSize is OPT-IN in picklist-sort because the default there must
    // reproduce a legacy PRINTED picklist byte-for-byte. That constraint does
    // not apply to this brand-new surface — and without it `Large` ranks 26
    // (after 5XL) and XXL/2XL split into two adjacent blocks. Do not "fix"
    // this back to raw getSizeOrder.
    const s =
      getSizeOrder(normalizeSize(a.category.size ?? '')) -
      getSizeOrder(normalizeSize(b.category.size ?? ''));
    if (s !== 0) return s;

    // Terminal rank 26 holds '' AND every unrecognised size (YS/YM/YL, raw
    // Shopify option text). Break that bucket alphabetically so youth sizes
    // stay together instead of scattering by SKU.
    const sr = cmpText(a.category.size ?? '', b.category.size ?? '');
    if (sr !== 0) return sr;
  }

  const sk = COLLATOR.compare(a.sku, b.sku);
  if (sk !== 0) return sk;

  // sensitivity:'base' can return 0 for distinct strings, and `sku` is not
  // unique across orgs at a shared location. `id` guarantees a total order, so
  // re-exporting the same data is byte-identical.
  return byCodepoint(a.id, b.id);
}

export function sortForStocktake<T extends StocktakeCsvSource>(rows: readonly T[]): T[] {
  return [...rows].sort(compareStocktakeRows);
}

/**
 * BLU-711 Part B: collapse the two halves of a substitution-mapped pair into
 * ONE count line. The pair shares a SKU CODE and a physical pile; two lines
 * would make the counter enter the same shelf quantity twice, and the importer
 * would then reject the file as a paired-duplicate. The OWNER half survives as
 * the stable import target (its sku id / Owner ID route the row); the backend
 * apportions the counted total back across both orgs' rows (client keeps
 * min(count, client_current), owner gets the remainder).
 *
 * Book stock on the collapsed line is the sum of the per-row clamps — a
 * negative balance on either half means nothing of that ledger is physically
 * on the shelf, matching the single-row clamp in buildStocktakeCsv.
 *
 * A pair with only ONE half in `rows` (filtered view, selected subset) passes
 * through unchanged — the importer handles the missing counterpart.
 */
export function collapseSharedPairs<T extends StocktakeCsvSource>(rows: readonly T[]): T[] {
  const rolesByPair = new Map<string, { client?: T; owner?: T }>();
  for (const row of rows) {
    if (!row.sharedPairId || !row.sharedPairRole) continue;
    const entry = rolesByPair.get(row.sharedPairId) ?? {};
    entry[row.sharedPairRole] = row;
    rolesByPair.set(row.sharedPairId, entry);
  }

  const out: T[] = [];
  for (const row of rows) {
    const pair = row.sharedPairId ? rolesByPair.get(row.sharedPairId) : undefined;
    if (!pair || !pair.client || !pair.owner) {
      out.push(row);
      continue;
    }
    // Both halves present: emit only when we reach the OWNER half, merged.
    if (row.sharedPairRole !== 'owner') continue;
    out.push({
      ...row,
      stockLevel: Math.max(0, pair.client.stockLevel) + Math.max(0, pair.owner.stockLevel),
    });
  }
  return out;
}

/**
 * Collapse newlines/tabs out of free-text cells.
 *
 * Load-bearing, not cosmetic: a multi-line cell makes the printed sheet
 * unreadable, and while the reader is now record-aware, flattening keeps the
 * artifact sane for anyone counting from paper.
 */
const flatten = (value: string): string => value.replace(/[\r\n\t]+/g, ' ').trim();

export function buildStocktakeCsv(
  rows: readonly StocktakeCsvSource[],
  options: BuildStocktakeCsvOptions,
): string {
  const {
    labels,
    locationName,
    ownerNameById,
    fallbackOwnerName,
    ownOrganizationId,
    includeCurrentCounts,
  } = options;

  // countSheetSize counts exactly these lines: keep the two in step.
  const body = sortForStocktake(collapseSharedPairs(countableRows(rows))).map((p) => {
    const ownerName =
      (p.ownerOrganizationId ? ownerNameById?.get(p.ownerOrganizationId) : undefined) ??
      fallbackOwnerName;

    return [
      p.sku,
      // Passed as a NUMBER so the formula guard's numeric exemption applies.
      // Clamped: a negative balance means nothing is physically on the shelf.
      includeCurrentCounts ? Math.max(0, p.stockLevel) : '',
      flatten(p.title ?? ''),
      labels.blockLabels[getStocktakeBlock(p)],
      flatten(p.category.type ?? ''),
      flatten(p.category.size ?? ''),
      flatten(p.category.color ?? ''),
      flatten(ownerName),
      // Authoritative routing key. Two orgs at one shared location can carry
      // the same display name, and the name column is editable by whoever
      // holds the sheet — the id is what the importer resolves on.
      p.ownerOrganizationId ?? ownOrganizationId ?? '',
      flatten(locationName),
    ];
  });

  // CRLF + BOM: Excel on Windows sniffs BOM-less UTF-8 as the system codepage
  // and mojibakes CJK titles — unreadable for exactly this feature's audience.
  return UTF8_BOM + toCsvContent([[...labels.headers], ...body], { eol: '\r\n' });
}

/** Deterministic fixture for the blank example sheet. */
export const EXAMPLE_STOCKTAKE_ROWS: readonly StocktakeCsvSource[] = [
  { id: 'x1', sku: 'TEE-S-BK', title: 'Classic Tee', stockLevel: 12, productionType: 'POD', category: { type: 'TEE', size: 'S', color: 'Black' } },
  { id: 'x2', sku: 'TEE-M-BK', title: 'Classic Tee', stockLevel: 8, productionType: 'POD', category: { type: 'TEE', size: 'M', color: 'Black' } },
  { id: 'x3', sku: 'TEE-L-BK', title: 'Classic Tee', stockLevel: 0, productionType: 'POD', category: { type: 'TEE', size: 'L', color: 'Black' } },
  { id: 'x4', sku: 'TEE-S-NT', title: 'Classic Tee', stockLevel: 5, productionType: 'POD', category: { type: 'TEE', size: 'S', color: 'Natural' } },
  { id: 'x5', sku: 'HOOD-M-GY', title: 'Pullover Hoodie', stockLevel: 3, productionType: 'POD', category: { type: 'HOOD', size: 'M', color: 'Grey' } },
  { id: 'x6', sku: 'AIRFRESH-01', title: 'Air Freshener', stockLevel: 40, productionType: 'PAP', category: { type: 'ACC' } },
  { id: 'x7', sku: 'TEE-M-WH-TH966-R4', title: 'Pre-printed Tee', stockLevel: 6, productionType: 'PAP', isPreprinted: true, category: { type: 'TEE', size: 'M', color: 'White' } },
  { id: 'x8', sku: 'LABEL-NECK-01', title: 'Neck Label', stockLevel: 500, productionType: 'PAP', isTrim: true, category: { type: 'TRIM' } },
];

export function buildExampleStocktakeCsv(options: BuildStocktakeCsvOptions): string {
  return buildStocktakeCsv(EXAMPLE_STOCKTAKE_ROWS, options);
}

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

/**
 * Filenames stay ASCII-slugged. A CJK location name slugs to the empty string
 * (`宁波仓库` -> ``), which previously produced `stock--2026-07-26.csv`; the
 * fallback token keeps the name meaningful.
 */
export function buildStocktakeFilename(
  locationName: string | undefined,
  variant: 'all' | 'selected' | 'example',
  today: string,
): string {
  if (variant === 'example') return 'count-sheet-example.csv';
  const slug = slugify(locationName ?? '') || 'location';
  return `count-sheet-${slug}-${variant}-${today}.csv`;
}
